'use client';
import { usePathname } from 'next/navigation';
import AlternateColors from '@repo/ui/src/components/AlternateColors';
import type { MeaterProductVariant } from '@src/domain/types/MeaterProduct';
import { findBestVariant } from './_variantUtils';

interface ColorVariantSelectorProps {
  variants: Array<MeaterProductVariant>;
  activeVariantId?: string;
}

const ColorVariantSelector = ({ variants, activeVariantId }: ColorVariantSelectorProps) => {
  const pathname = usePathname();

  const activeVariant = variants.find((v) => v.id === activeVariantId);

  const colorVariants = variants.reduce<
    Array<{ label: string; swatchColor?: string; isAvailable: boolean; href: string }>
  >((acc, v) => {
    const colorOpt = v.selectedOptions.find((o) => o.name.toLowerCase() === 'color');
    if (!colorOpt || acc.some((c) => c.label === colorOpt.value)) return acc;
    const target = findBestVariant(variants, activeVariant, 'color', colorOpt.value) ?? v;
    return [
      ...acc,
      {
        label: colorOpt.value,
        swatchColor: target.colorValue,
        isAvailable: target.availableForSale,
        href: `${pathname}?variant=${target.variantId}`,
      },
    ];
  }, []).sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable));

  if (colorVariants.length <= 1) return null;

  const activeColor =
    activeVariant?.selectedOptions.find((o) => o.name.toLowerCase() === 'color')?.value ?? '';

  return <AlternateColors activeColor={activeColor} colors={colorVariants} />;
};

export default ColorVariantSelector;
